<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div>
          <p class="text-uppercase text-muted mb-1 page-kicker">Account</p>
          <h2 class="h3 mb-0">My Sites</h2>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-end">
            <div class="col-lg-8">
              <p class="page-kicker mb-2">Site Selection</p>
              <h3 class="mb-2">Choose which sites you want to see</h3>
              <p class="text-muted mb-0">
                Only your selected sites will appear across sessions, reports, and dashboards.
                If no sites are selected, all views will be empty until you save a selection.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border-0">
        <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <h3 class="mb-0">Available Sites</h3>
          <span class="badge badge-pill badge-primary">{{ selectedIds.length }} selected</span>
        </div>

        <div v-if="loading" class="text-center py-6">
          <b-spinner variant="primary" class="mb-3"></b-spinner>
          <p class="text-muted mb-0">Loading sites...</p>
        </div>

        <div v-else-if="allSites.length === 0" class="text-center py-6">
          <h4 class="mb-2">No sites available</h4>
          <p class="text-muted mb-0">Contact your administrator if you expect to see sites here.</p>
        </div>

        <div v-else class="card-body py-3">
          <p class="text-muted small mb-3">
            <i class="fas fa-hand-pointer mr-1"></i>
            Click a card to select or deselect it, then press <strong>Save Selection</strong> to apply your changes.
          </p>
          <div class="site-grid">
            <div
              v-for="site in allSites"
              :key="site.id"
              class="site-tile"
              :class="{ 'is-selected': isSelected(site.id) }"
              @click="toggleSite(site.id)"
            >
              <div class="site-tile-check">
                <i v-if="isSelected(site.id)" class="fas fa-check-circle text-primary"></i>
                <i v-else class="far fa-circle text-muted"></i>
              </div>
              <div class="site-tile-body">
                <h5 class="mb-1">{{ site.name }}</h5>
                <p class="text-muted mb-0 small">{{ site.fullname }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom padding so content doesn't hide behind sticky bar -->
      <div style="height: 5rem;"></div>
    </div>

    <!-- Sticky save bar — always visible -->
    <div v-if="!loading && allSites.length" class="site-save-bar">
      <div class="container-fluid d-flex align-items-center justify-content-between flex-wrap" style="gap: 0.5rem;">
        <span class="text-muted small">
          <span v-if="selectedIds.length === 0">No sites selected — views will be empty until you save.</span>
          <span v-else><strong>{{ selectedIds.length }}</strong> of {{ allSites.length }} sites selected</span>
        </span>
        <div class="d-flex" style="gap: 0.5rem;">
          <b-button variant="outline-secondary" size="sm" @click="clearAll">Clear All</b-button>
          <b-button variant="outline-secondary" size="sm" @click="selectAll">Select All</b-button>
          <b-button variant="primary" size="sm" :disabled="saving" @click="save">
            <b-spinner v-if="saving" small class="mr-1"></b-spinner>
            {{ saving ? 'Saving...' : 'Save Selection' }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import userData from '@/pages/Common/userData'

export default {
  components: { userData },
  data() {
    return {
      loading: true,
      saving: false,
      selectedIds: []
    }
  },
  computed: {
    ...mapGetters('sitePreferences', ['allSites', 'userPreferences'])
  },
  methods: {
    ...mapActions('sitePreferences', [
      'FETCH_ALL_SITES',
      'FETCH_USER_PREFERENCES',
      'UPDATE_USER_PREFERENCES'
    ]),
    isSelected(id) {
      return this.selectedIds.includes(id)
    },
    toggleSite(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx === -1) {
        this.selectedIds.push(id)
      } else {
        this.selectedIds.splice(idx, 1)
      }
    },
    selectAll() {
      this.selectedIds = this.allSites.map(s => s.id)
    },
    clearAll() {
      this.selectedIds = []
    },
    save() {
      this.saving = true
      this.UPDATE_USER_PREFERENCES(this.selectedIds)
        .then(() => {
          this.SUCCESS_TOAST_MSG('Site selection saved')
          if (this.$route.query.redirect) {
            this.$router.replace(this.$route.query.redirect)
          }
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false
        })
    }
  },
  created() {
    Promise.all([
      this.FETCH_ALL_SITES(),
      this.FETCH_USER_PREFERENCES()
    ])
      .then(() => {
        this.selectedIds = this.userPreferences.map(s => s.id)
      })
      .finally(() => {
        this.loading = false
      })
  }
}
</script>

<style scoped>
.page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}

.site-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}

.site-tile {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border: 1px solid #e7ebf4;
  border-radius: 0.85rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  user-select: none;
}

.site-tile:hover {
  border-color: #a5b4fc;
  box-shadow: 0 4px 12px rgba(94, 114, 228, 0.08);
}

.site-tile.is-selected {
  background: #eef2ff;
  border-color: #5e72e4;
}

.site-tile-check {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.site-tile-body h5 {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
}

.site-tile-body p {
  font-size: 0.78rem;
}

.site-save-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e7ebf4;
  padding: 0.75rem 1.5rem;
  z-index: 1030;
  box-shadow: 0 -4px 16px rgba(50, 50, 93, 0.08);
}
</style>



// WEBPACK FOOTER //
// src/pages/Admin/sitePreferences.vue